/** "Trazer do cliente, invertido" — o pop-up da aba Foco quando o alvo do
 *  cadastro é um concorrente (item 32 da fila, 2026-11-01).
 *
 *  O foco do concorrente é o espelho do foco do cliente: o que é "Foco
 *  cliente" na empresa ("de quem a matéria fala") passa a ser "Foco
 *  concorrente" na sombra dele ("quem não é o sujeito"), e vice-versa.
 *  Montar isso à mão, termo a termo, era a queixa; aqui a pessoa lê o foco
 *  do cliente já invertido e marca o que quer levar.
 *
 *  **Só monta a lista e devolve a seleção.** Quem grava é a aba (um POST por
 *  termo, na sequência, e a recarga ANTES da mensagem), porque ela é dona do
 *  `catalogApi` mirado no concorrente e da regra de erro. */

import { useEffect, useMemo, useState } from "react";

import type { BrandOut, FocusCategory, FocusTerm } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { Select } from "@/shared/components/Select";
import { foldText } from "@/shared/utils/text-search";

import { SettingsFormDialog } from "./SettingsFormDialog";

export type InvertedTerm = { category: FocusCategory; term: string };

type Candidate = InvertedTerm & { key: string; existing: boolean; onOtherSide: boolean };

const OPPOSITE: Record<FocusCategory, FocusCategory> = { client: "competitor", competitor: "client" };

const keyOf = (category: FocusCategory, term: string) => `${category}|${foldText(term.trim())}`;

type Props = {
  sections: readonly { key: FocusCategory; label: string }[];
  /** Marcas ATIVAS do cliente (a origem). */
  clientBrands: readonly BrandOut[];
  /** Os termos de foco do cliente de uma marca, na fonte da aba — sem `competitor_id`. */
  readClientTerms: (brandId: number) => Promise<FocusTerm[]>;
  /** O que o concorrente já tem (marca e fonte da aba): vira "já cadastrado". */
  existing: readonly FocusTerm[];
  targetBrandName: string;
  sourceLabel: string;
  busy: boolean;
  onApply: (items: InvertedTerm[]) => void;
  onClose: () => void;
};

export function FocusInvertDialog({
  sections,
  clientBrands,
  readClientTerms,
  existing,
  targetBrandName,
  sourceLabel,
  busy,
  onApply,
  onClose
}: Props) {
  const [brandId, setBrandId] = useState<number | null>(
    () => (clientBrands.find(b => b.is_main_brand) ?? clientBrands[0])?.id ?? null
  );
  const [clientTerms, setClientTerms] = useState<FocusTerm[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Sobrescritas por clique; sem entrada vale o padrão (marcado, salvo o que o
  // concorrente já tem do outro lado, que nasce desmarcado).
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    if (brandId === null) {
      setClientTerms([]);
      return;
    }
    let alive = true;
    setClientTerms(null);
    setLoadError(null);
    readClientTerms(brandId)
      .then(rows => {
        if (!alive) return;
        setClientTerms(rows);
        setOverrides(new Map());
      })
      .catch(error => {
        if (!alive) return;
        setClientTerms([]);
        setLoadError(errorMessage(error, "Não foi possível ler o foco do cliente."));
      });
    return () => {
      alive = false;
    };
    // `readClientTerms` é uma closure nova a cada render da aba; a leitura
    // depende só da marca escolhida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const candidates = useMemo<Candidate[]>(() => {
    const have = new Set(
      existing.filter(t => t.is_active !== false).map(t => keyOf(t.category, t.term))
    );
    const seen = new Set<string>();
    const out: Candidate[] = [];
    for (const row of clientTerms ?? []) {
      if (row.is_active === false) continue;
      const category = OPPOSITE[row.category];
      const key = keyOf(category, row.term);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        category,
        term: row.term,
        existing: have.has(key),
        // Já está na categoria OPOSTA da sombra: trazer poria o termo nos dois
        // lados dela. Nasce desmarcado, mas segue marcável (escolha, não erro).
        onOtherSide: have.has(keyOf(OPPOSITE[category], row.term))
      });
    }
    return out;
  }, [clientTerms, existing]);

  const selectable = candidates.filter(c => !c.existing);
  const isMarked = (c: Candidate) => overrides.get(c.key) ?? !c.onOtherSide;
  const selected = selectable.filter(isMarked);
  const allMarked = selected.length === selectable.length;

  const toggle = (candidate: Candidate) =>
    setOverrides(prev => {
      const next = new Map(prev);
      next.set(candidate.key, !isMarked(candidate));
      return next;
    });

  return (
    <SettingsFormDialog
      eyebrow="FOCO"
      title="Trazer do cliente, invertido"
      description={`Destino: ${targetBrandName} · ${sourceLabel}`}
      editing={false}
      values={[...overrides].map(([k, v]) => `${k}=${v}`).sort()}
      busy={busy}
      required={[{ label: "Ao menos um termo marcado", filled: selected.length > 0 }]}
      saveLabel={`Aplicar (${selected.length})`}
      error={loadError}
      onSave={() => onApply(selected.map(({ category, term }) => ({ category, term })))}
      onClose={onClose}
    >
      <p className="section-hint">
        O foco do cliente entra espelhado: o que é <strong>Foco cliente</strong> lá vira{" "}
        <strong>Foco concorrente</strong> aqui, e o <strong>Foco concorrente</strong> do
        cliente vira <strong>Foco cliente</strong> aqui. Revise antes de aplicar: com mais de
        um concorrente, o Foco concorrente do cliente mistura os termos de todos eles, e nem
        todos são deste. O termo que o concorrente já tem no outro lado vem desmarcado, pra não
        cair nos dois lados sem querer.
      </p>

      <label className="form-field">
        <span>Marca do cliente</span>
        <Select
          value={brandId === null ? "" : String(brandId)}
          onChange={event => setBrandId(Number(event.target.value))}
          disabled={busy || clientBrands.length === 0}
        >
          {clientBrands.map(brand => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
              {brand.is_main_brand ? " (principal)" : ""}
            </option>
          ))}
        </Select>
      </label>

      {clientTerms === null ? (
        <p className="section-hint">Lendo o foco do cliente…</p>
      ) : candidates.length === 0 ? (
        <p className="section-hint">O cliente não tem foco cadastrado nesta marca e fonte.</p>
      ) : (
        <>
          <div className="panel-header-actions">
            <button
              type="button"
              className="secondary-button"
              disabled={busy || selectable.length === 0}
              onClick={() =>
                setOverrides(new Map(selectable.map(c => [c.key, !allMarked])))
              }
            >
              {allMarked ? "Desmarcar todas" : "Marcar todas"}
            </button>
          </div>
          {sections.map(section => {
            const group = candidates.filter(c => c.category === section.key);
            return (
              <div className="form-field" key={section.key}>
                <span>{section.label}</span>
                {group.length === 0 ? (
                  <small>Nenhum termo para trazer.</small>
                ) : (
                  group.map(candidate => (
                    <label className="form-field-inline" key={candidate.key}>
                      <input
                        type="checkbox"
                        checked={!candidate.existing && isMarked(candidate)}
                        disabled={busy || candidate.existing}
                        onChange={() => toggle(candidate)}
                      />
                      {candidate.term}
                      {candidate.existing && <small> — já cadastrado</small>}
                      {!candidate.existing && candidate.onOtherSide && (
                        <small>
                          {" "}
                          — já está no outro lado (
                          {sections.find(x => x.key === OPPOSITE[candidate.category])?.label})
                        </small>
                      )}
                    </label>
                  ))
                )}
              </div>
            );
          })}
        </>
      )}
    </SettingsFormDialog>
  );
}
